import { Button, SearchAutoComplete, SearchItem } from '@/components';
import { UserBaseInfo } from '@/modules/Users/domain';
import { Role } from '@/modules/Users/domain/role';
import { RoleEntity } from '@/modules/Users/types/role';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UsersRoleDropdown } from '../UsersRoleDropdown';

type AssociateUserFormProps = {
  onClose: () => void;
  onSubmit: (items: SearchItem<{ role?: number }>[]) => void;
  userSearch: string;
  onUserSearch: (search: string) => void;
  users: UserBaseInfo[];
  isSubmitting?: boolean;
  roles?: Role[];
  roleEntity?: RoleEntity;
};

export const AssociateUserForm = ({
  users,
  userSearch,
  onUserSearch,
  onSubmit,
  onClose,
  roles,
  isSubmitting,
  roleEntity,
}: AssociateUserFormProps) => {
  const { t } = useTranslation();

  const [selectedUsers, setSelectedUsers] = useState<
    Array<{ id: number; value: string; role?: number }>
  >([]);

  const items = useMemo(() => {
    return users
      ? users.map((user) => ({
          id: user.id,
          value: user.email,
        }))
      : [];
  }, [users]);

  const handleOnSelect = useCallback(
    (item: SearchItem) => {
      setSelectedUsers((prevState: Array<SearchItem>) => [...prevState, item]);
      onUserSearch('');
    },
    [onUserSearch],
  );

  const deselectUser = useCallback((index: number) => {
    setSelectedUsers((prevState: Array<SearchItem>) =>
      prevState.filter((_, i) => i !== index),
    );
  }, []);

  const selectRole = useCallback((roleId: number, userId: number) => {
    setSelectedUsers((prevState: Array<SearchItem>) =>
      prevState.map((user) =>
        user.id === userId ? { ...user, role: roleId } : user,
      ),
    );
  }, []);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
      }}
    >
      <div className="mt-2 flex h-112 flex-col overflow-y-auto p-2">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col">
            <span className="mb-3 text-sm text-blueNuit">
              {t('add_existing_user')}
            </span>
            <div className="mb-2 text-sm font-bold capitalize">
              {t('search_existing_user_by_email')}
            </div>
            <div className="flex h-full w-full flex-row items-center justify-between gap-4">
              <div className="mb-3 mt-2 h-full flex-1">
                <SearchAutoComplete
                  value={userSearch}
                  options={items}
                  onChange={onUserSearch}
                  onSelect={handleOnSelect}
                />
              </div>
            </div>
          </div>
          <div>
            <UsersRoleDropdown
              items={selectedUsers}
              roles={roles ?? []}
              deleteUser={deselectUser}
              selectRole={selectRole}
              roleEntity={roleEntity}
            />
          </div>
        </div>
      </div>
      <div className="mt-8 flex justify-between">
        <Button onClick={onClose} variant="outline" className="h-9 w-24">
          {t('cancel')}
        </Button>
        <Button
          buttonType="button"
          onClick={(event) => {
            event.preventDefault();
            onSubmit(selectedUsers);
          }}
          className="h-9 w-24"
          isDisabled={
            isSubmitting ||
            !selectedUsers.length ||
            (!!roles?.length && selectedUsers.some((u) => !u.role))
          }
        >
          {t('add')}
        </Button>
      </div>
    </form>
  );
};
